import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants/networking.constants';
import {
  CountriesDataPagingRes,
  Country,
} from '@/shared/interfaces/erp/system-settings';
import { getUrlParamsFetch } from '@/shared/utils';
import { useUiStore } from '@/store/ui';
import { NavigateFunction } from 'react-router-dom';

const { get, post, put, remove } = erpApi();

///* tanSctack query
export const useFetchCountries = (page = 1, nombre = '', page_size = 10) => {
  return useQuery({
    queryKey: ['countries', page, nombre],
    queryFn: async () => {
      return getCountriesFetch({ page, nombre, page_size });
    },
  });
};
export const useFetchAllCountries = (
  params?: getCountriesParams,
  enabled = true
) => {
  return useQuery({
    queryKey: ['countries', ...Object.values(params ?? {})],
    queryFn: async () => {
      return getCountriesFetch(params);
    },
    enabled,
    refetchOnWindowFocus: false,
  });
};

export const useGetCountry = (id: string) => {
  return useQuery({
    queryKey: ['countries', id],
    queryFn: async () => {
      return getCountry(id);
    },
  });
};

export const useCreateCountry = (
  navigate: NavigateFunction,
  returnUrl: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createCountry,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['countries'] });
      navigate(returnUrl);
    },
  });
};

export const useUpdateCountry = (
  navigate: NavigateFunction,
  returnUrl: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateCountry,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['countries'] });
      navigate(returnUrl);
    },
  });
};

export const useRemoveCountry = (
  navigate: NavigateFunction,
  returnUrl: string
) => {
  const queryClient = useQueryClient();
  const setConfirmDialogIsOpen = useUiStore.getState().setConfirmDialogIsOpen;

  return useMutation({
    mutationFn: removeCountry,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['countries'] });
      navigate(returnUrl);
    },
    onSettled: () => {
      setConfirmDialogIsOpen(false);
    },
  });
};

///* axios
export type getCountriesParams = Partial<Country> & {
  page?: number;
  page_size?: number;

  filterByState?: boolean;
};

export type CreateCountryParams = Omit<Country, 'id_pais'>;
export type UpdateCountryParams = {
  id: string;
  data: CreateCountryParams;
};


export const getCountriesFetch = async (params?: getCountriesParams) => {
  const stateParams = { ...params };

  // filter by state
  if (stateParams.filterByState === false && stateParams.estado === undefined) {
    delete stateParams.estado;
  } else if (stateParams.filterByState !== false) {
    stateParams.estado = true;
  }
  delete stateParams.filterByState;

  const queryParams = getUrlParamsFetch(stateParams);
  return get<CountriesDataPagingRes>(
    `${urlBackend}api/v1/pais/?${queryParams}`,
    true
  );
};
export const useFetchCountriesFetch = (params?: getCountriesParams) => {
  return useQuery({
    queryKey: ['countries', ...Object.values(params ?? {})],
    queryFn: async () => {
      return getCountriesFetch(params);
    },
  });
};

export const getCountry = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await get<Country>(`${urlBackend}api/v1/pais/${id}/`, true);

  res.status === 'error' && setAlert(true, res.message, 'error');
  return res;
};

export const createCountry = async (data: CreateCountryParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await post<Country>(`${urlBackend}api/v1/pais/`, data, true);

  setAlert(true, res.message, res.status);
  return res;
};

export const updateCountry = async ({ id, data }: UpdateCountryParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await put<Country>(`${urlBackend}api/v1/pais/${id}/`, data, true);

  setAlert(true, res.message, res.status);
  return res;
};

export const removeCountry = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await remove<Country>(`${urlBackend}api/v1/pais/${id}/`, true);

  setAlert(true, res.message, res.status);
  return res;
};
